export type HomepageSectionDefinition = {
  key: string;
  label: string;
  description: string;
  defaultPayload: Record<string, unknown>;
  order: number;
};

export const HOMEPAGE_SECTION_DEFINITIONS: HomepageSectionDefinition[] = [
  {
    key: "hero",
    label: "Hero",
    description: "Top-of-page brand statement and supporting copy.",
    order: 0,
    defaultPayload: {
      heroMode: "video",
      brandName: "Pearl Voyages Travel",
      title: "Explore Uganda. Discover Europe",
      subtitle: "Beautiful Journeys Across Continents",
      supportTitle: "Connecting Your Journey Across Continents",
      supportCopy:
        "Pearl Voyages Travel connects you to each destination through local expertise, global standards, and a customer-first approach that shapes every journey around you.",
      backgroundImage: "https://cdn.pearlvoyages.com/home-page/hero-image-lion.webp",
      videoUrl: "https://cdn.pearlvoyages.com/home-page/video.mp4",
    },
  },
  {
    key: "countries-we-operate",
    label: "Connecting You to African Safaris and Europe Tours Seamlessly",
    description: "The two-region operating overview with country tabs and route framing.",
    order: 1,
    defaultPayload: {
      eyebrow: "Bridging African Safaris and Europe Tours With Expert Planning",
      title: "Bridging African Safaris and Europe Tours With Expert Planning",
      description:
        "We design seamless travel experiences across Africa and Europe, combining wildlife, culture, business, education, and Schengen-linked travel routes.",
      strategicLine:
        "From East Africa’s raw natural beauty to Europe’s cultural depth, every journey is structured to move effortlessly across continents.",
      regions: [
        {
          label: "Africa",
          value: "Uganda · Rwanda · Kenya · Tanzania",
        },
        {
          label: "Europe (Schengen Region)",
          value:
            "Austria · Belgium · Bulgaria · Croatia · Czech Republic · Denmark · Estonia · Finland · France · Germany · Greece · Hungary · Iceland · Italy · Latvia · Liechtenstein · Lithuania · Luxembourg · Malta · Netherlands · Norway · Poland · Portugal · Romania · Slovakia · Slovenia · Spain · Sweden · Switzerland",
        },
      ],
      tabOrder: [
        "Uganda",
        "Rwanda",
        "Kenya",
        "Tanzania",
        "Germany",
        "France",
        "Italy",
        "Spain",
        "Netherlands",
        "Belgium",
        "Austria",
        "Switzerland",
      ],
      countries: [
        {
          region: "East Africa",
          country: "Uganda",
          focus:
            "Wildlife safaris, gorilla trekking, cultural immersion, and luxury nature experiences shaped around intimacy and route clarity.",
          image:
            "https://images.unsplash.com/photo-1527719327859-c6ce80353573?auto=format&fit=crop&w=1600&q=80",
          ghostLeft: "UGA",
          ghostRight: "NDA",
        },
        {
          region: "East Africa",
          country: "Rwanda",
          focus:
            "Mountain experiences, refined primate journeys, curated hospitality, and highly composed travel moments with emotional depth.",
          image:
            "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1600&q=80",
          ghostLeft: "RWA",
          ghostRight: "NDA",
        },
        {
          region: "East Africa",
          country: "Kenya",
          focus:
            "Wildlife-rich plains, iconic safari landscapes, migration drama, and premium lodge-based journeys with strong visual rhythm.",
          image:
            "https://images.unsplash.com/photo-1549366021-9f761d450615?auto=format&fit=crop&w=1600&q=80",
          ghostLeft: "KEN",
          ghostRight: "YA",
        },
        {
          region: "East Africa",
          country: "Tanzania",
          focus:
            "Cinematic safari scale, island extensions, expansive plains, and expertly sequenced journeys built around seasonality and pace.",
          image:
            "https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?auto=format&fit=crop&w=1600&q=80",
          ghostLeft: "TAN",
          ghostRight: "ZANIA",
        },
        {
          region: "Europe",
          country: "Germany",
          focus:
            "Cultural travel, city experiences, education travel, business travel, and Schengen travel planning with structured logistics.",
          image:
            "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?auto=format&fit=crop&w=1600&q=80",
          ghostLeft: "GER",
          ghostRight: "MANY",
        },
        {
          region: "Europe",
          country: "France",
          focus:
            "Museum culture, culinary discovery, city elegance, education pathways, and seamless European travel coordination.",
          image:
            "https://images.unsplash.com/photo-1431274172761-fca41d930114?auto=format&fit=crop&w=1600&q=80",
          ghostLeft: "FRA",
          ghostRight: "NCE",
        },
        {
          region: "Europe",
          country: "Italy",
          focus:
            "Historic cities, art-led travel, immersive culture, business-linked travel, and premium itinerary planning across regions.",
          image:
            "https://images.unsplash.com/photo-1525874684015-58379d421a52?auto=format&fit=crop&w=1600&q=80",
          ghostLeft: "ITA",
          ghostRight: "LY",
        },
        {
          region: "Europe",
          country: "Spain",
          focus:
            "Urban energy, cultural heritage, educational visits, and multi-city travel designed with flexibility and confidence.",
          image:
            "https://images.unsplash.com/photo-1543783207-ec64e4d95325?auto=format&fit=crop&w=1600&q=80",
          ghostLeft: "SPA",
          ghostRight: "IN",
        },
        {
          region: "Europe",
          country: "Netherlands",
          focus:
            "City-based discovery, education and business routes, museum culture, and efficient Schengen-connected travel experiences.",
          image:
            "https://images.unsplash.com/photo-1512470876302-972faa2aa9a4?auto=format&fit=crop&w=1600&q=80",
          ghostLeft: "NETH",
          ghostRight: "ERLANDS",
        },
        {
          region: "Europe",
          country: "Belgium",
          focus:
            "Urban culture, diplomatic and business travel relevance, education-linked movement, and premium European coordination.",
          image:
            "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=1600&q=80",
          ghostLeft: "BEL",
          ghostRight: "GIUM",
        },
        {
          region: "Europe",
          country: "Austria",
          focus:
            "Heritage, classical culture, mountain-linked travel, and structured itineraries for leisure, education, and business visitors.",
          image:
            "https://images.unsplash.com/photo-1516550893923-42d28e5677af?auto=format&fit=crop&w=1600&q=80",
          ghostLeft: "AUS",
          ghostRight: "TRIA",
        },
        {
          region: "Europe",
          country: "Switzerland",
          focus:
            "Alpine elegance, high-trust travel environments, education and business alignment, and carefully managed luxury itineraries.",
          image:
            "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=1600&q=80",
          ghostLeft: "SWIT",
          ghostRight: "ZERLAND",
        },
      ],
    },
  },
  {
    key: "africa-europe-bridge",
    label: "Africa-Europe Bridge",
    description: "Cross-continental bridge section showcasing Africa and Europe regional expertise with animated route.",
    order: 2,
    defaultPayload: {
      badge: "Simplified travel between Africa and Europe",
      title: "Bridging African Safaris and Europe Tours With Expert Planning",
      description:
        "Pearl Voyages Travel is a premier travel and tourism company connecting Uganda and Europe with main focus on the Schengen region through curated, high-quality travel experiences. Inspired by the beauty of Africa and the richness of Europe, we design journeys that are seamless, immersive, and culturally meaningful.",
      africaLabel: "Africa",
      africaTitle: "East Africa Departures",
      africaDescription:
        "Discover well-structured safari and regional travel experiences shaped around comfort, timing, destination flow, and trusted on-ground coordination.",
      africaCountries: ["Uganda", "Rwanda", "Kenya", "Tanzania"],
      europeLabel: "Europe (Schengen Region)",
      europeTitle: "Connected European Arrivals",
      europeDescription:
        "Move into multi-country Europe itineraries with more clarity, from entry planning and route structure to culturally rich city, business, and educational travel experiences.",
      europePrimaryCountries: [
        "Austria",
        "Belgium",
        "Bulgaria",
        "Croatia",
        "Czech Republic",
      ],
      europeMoreCountries: [
        "Denmark",
        "Estonia",
        "Finland",
        "France",
        "Germany",
        "Greece",
        "Hungary",
        "Iceland",
        "Italy",
        "Latvia",
        "Liechtenstein",
        "Lithuania",
        "Luxembourg",
        "Malta",
        "Netherlands",
        "Norway",
        "Poland",
        "Portugal",
        "Romania",
        "Slovakia",
        "Slovenia",
        "Spain",
        "Sweden",
        "Switzerland",
      ],
      europeCountries: [
        "Austria",
        "Belgium",
        "Bulgaria",
        "Croatia",
        "Czech Republic",
        "Denmark",
        "Estonia",
        "Finland",
        "France",
        "Germany",
        "Greece",
        "Hungary",
        "Iceland",
        "Italy",
        "Latvia",
        "Liechtenstein",
        "Lithuania",
        "Luxembourg",
        "Malta",
        "Netherlands",
        "Norway",
        "Poland",
        "Portugal",
        "Romania",
        "Slovakia",
        "Slovenia",
        "Spain",
        "Sweden",
        "Switzerland",
      ],
      europeShowMoreLabel: "See More Schengen Countries",
      europeShowLessLabel: "Show Less Schengen Countries",
      supportingCopy:
        "We specialize in both inbound tourism to Uganda and outbound travel to Europe, offering personalized, reliable, and memorable travel solutions for individuals, groups, and institutions.",
      tags: ["Wildlife", "Culture", "Business", "Education", "Multi-country Routes"],
      ctaButtonText: "Plan Your Cross-Continent Journey",
      features: [
        {
          title: "Structured Travel Flow",
          text: "From departure to arrival, each leg is sequenced for a smoother cross-continent experience.",
        },
        {
          title: "Premium Route Coordination",
          text: "We align destination planning with timing, comfort, and practical travel requirements.",
        },
        {
          title: "Shared Value for Travellers",
          text: "You gain clearer planning, stronger support, and journeys designed to feel seamless end to end.",
        },
      ],
    },
  },
  {
    key: "intro",
    label: "Intro Statement",
    description: "The central brand statement below the country block.",
    order: 3,
    defaultPayload: {
      title: "Travel Beyond Borders, Designed Around You",
      paragraphs: [
        "You are not looking for a trip.",
        "You are looking for a journey that feels effortless and worth remembering.",
        "Pearl Voyages Travel connects Uganda and Europe through carefully structured experiences. Every journey is planned with precision, ensuring clarity, comfort, and confidence from arrival to return.",
        "Whether exploring East Africa or traveling to Europe, each experience is shaped around your goals, pace, and expectations.",
        "We combine local insight with global standards to deliver travel that is reliable, structured, and refined.",
      ],
    },
  },
  {
    key: "values",
    label: "Values",
    description: "The value proposition card grid.",
    order: 4,
    defaultPayload: {
      heading: {
        eyebrow: "Why Choose Us",
        title: "What Makes Pearl Voyages Travel Different",
        description:
          "We specialize in two-way Uganda-Europe travel with local expertise, global standards, and personalized planning before, during, and after your journey.",
      },
      items: [
        { icon: "Globe", title: "Two-Way Expertise", description: "We coordinate both inbound Uganda tourism and outbound Europe travel experiences." },
        { icon: "MapPinned", title: "Local + Global", description: "Deep Uganda on-ground knowledge combined with trusted European travel coordination." },
        { icon: "SlidersHorizontal", title: "Personalized Planning", description: "Trips are tailored to traveler profile, pace, budget, and purpose." },
        { icon: "Headphones", title: "Reliable Support", description: "Dedicated support before departure, while traveling, and after your journey." },
      ],
    },
  },
  {
    key: "featured-products",
    label: "Featured Experiences",
    description: "The featured destination and experience headline.",
    order: 5,
    defaultPayload: {
      heading: {
        eyebrow: "Featured Journeys",
        title: "Featured Travel Experiences",
        description:
          "Selected journeys designed for immersive, reliable, and professionally coordinated travel between Uganda and Europe.",
      },
    },
  },
  {
    key: "industries",
    label: "Service Areas",
    description: "The service category card grid.",
    order: 6,
    defaultPayload: {
      heading: {
        eyebrow: "Core Service Areas",
        title: "Pearl Voyages Travel Experiences",
        description:
          "Cross-continental journeys connecting Uganda and Europe through curated tourism, visa-supported travel, and exchange-focused programs.",
      },
      items: [
        { icon: "TreePine", title: "Uganda Inbound Tourism", description: "Gorilla trekking, wildlife safaris, adventure travel, and cultural tours across Uganda." },
        { icon: "Plane", title: "Europe Travel Support", description: "Schengen-focused travel planning with visa guidance, itinerary design, and departure support." },
        { icon: "Users", title: "Group & Family Travel", description: "Coordinated holiday trips for families, friends, corporates, and institutions." },
        { icon: "GraduationCap", title: "Business & Education Travel", description: "Structured travel for conferences, study tours, partnerships, and professional exchange." },
      ],
    },
  },
  {
    key: "products",
    label: "Travel Packages",
    description: "The package grid and bottom CTA.",
    order: 7,
    defaultPayload: {
      heading: {
        eyebrow: "Our Core Services",
        title: "Travel Packages & Programs",
        description:
          "Beautiful journeys across continents designed for individuals, groups, institutions, and diaspora communities.",
      },
      items: [
        { icon: "Binoculars", title: "Gorilla Trekking Experiences", description: "Guided rainforest treks in southwestern Uganda with optional cultural and nature add-ons.", link: "/packages?category=gorilla-trekking" },
        { icon: "Camera", title: "Safari Packages", description: "Immersive safaris in Uganda's iconic parks with flexible accommodation tiers.", link: "/packages?category=safari" },
        { icon: "Landmark", title: "Cultural Tours", description: "Community-based experiences that highlight Uganda's heritage, traditions, and local stories.", link: "/packages?category=cultural-tours" },
        { icon: "Sparkles", title: "Luxury & Eco-Tourism", description: "Premium and sustainable journeys for honeymooners, families, and high-end experiential travelers.", link: "/packages?category=luxury-eco" },
      ],
      bottomCtaText: "Explore All Travel Packages",
      bottomCtaLink: "/packages",
    },
  },
  {
    key: "advantages",
    label: "Strategic Advantage",
    description: "The dark themed differentiator card grid.",
    order: 8,
    defaultPayload: {
      heading: {
        eyebrow: "Strategic Advantage",
        title: "Why Choose Pearl Voyages Travel",
        description:
          "We are a bi-directional travel facilitator bridging Uganda and Europe with structured, trusted, and scalable travel services.",
      },
      items: [
        { icon: "ArrowLeftRight", title: "Bi-Directional Travel Expertise", description: "Unique focus on Europe-Africa and Africa-Europe journeys, not one-way tour operations." },
        { icon: "FileCheck2", title: "Visa-Supported Planning", description: "Structured support for Schengen travel documentation, planning, and confidence-building." },
        { icon: "Handshake", title: "Partnership-Driven Delivery", description: "Reliable partner network across destinations, accommodations, and on-ground operators." },
        { icon: "Globe2", title: "Cross-Continental Reach", description: "Programs spanning tourism, diaspora travel, business visits, and educational exchange." },
        { icon: "Leaf", title: "Responsible Tourism", description: "Commitment to community support, eco-conscious travel, and cultural heritage preservation." },
        { icon: "Headphones", title: "End-to-End Support", description: "Dedicated support before, during, and after travel for individuals, groups, and institutions." },
      ],
    },
  },
  {
    key: "faq",
    label: "FAQ",
    description: "The frequently asked questions accordion.",
    order: 9,
    defaultPayload: {
      heading: {
        eyebrow: "Common Questions",
        title: "Frequently Asked Questions",
        description:
          "Answers to common questions from travelers, groups, and institutions planning journeys with Pearl Voyages Travel.",
      },
      items: [
        { title: "Who do you serve?", description: "We serve international tourists, Ugandan travelers, corporate teams, NGOs, diaspora communities, and educational institutions." },
        { title: "Do you support visa-related travel planning?", description: "Yes. For Europe-bound travel, we provide step-by-step visa guidance, documentation support, and itinerary planning." },
        { title: "Can trips be customized?", description: "Yes. We design tailor-made itineraries based on travel goals, budget, pace, and preferred experiences." },
        { title: "Do you offer both Uganda and Europe travel experiences?", description: "Yes. We specialize in inbound Uganda tourism and outbound Europe travel, especially Schengen-region coordination." },
      ],
    },
  },
  {
    key: "video",
    label: "Video",
    description: "The process explainer and lightbox thumbnail.",
    order: 10,
    defaultPayload: {
      heading: {
        eyebrow: "How We Work",
        title: "Cross-Continental Travel Planning",
        description:
          "See how Pearl Voyages Travel designs, coordinates, and delivers seamless travel experiences between Uganda and Europe.",
      },
      videoUrl: "https://cdn.pearlvoyages.com/home-page/video.mp4",
      thumbnailUrl: "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1800&q=80",
      videoCaption: "Pearl Voyages Travel · Travel Planning Overview",
    },
  },
  {
    key: "cta",
    label: "Call To Action",
    description: "The final conversion block.",
    order: 11,
    defaultPayload: {
      heading: {
        eyebrow: "Ready to Start?",
        title: "Start Your Pearl Voyages Travel Journey",
        description:
          "Share your destination, travel dates, and goals. Our team will craft a seamless Uganda-Europe travel plan tailored to your needs.",
      },
      items: [
        { icon: "MapPinned", label: "Curated Itineraries" },
        { icon: "FileCheck2", label: "Visa Guidance Support" },
        { icon: "Headphones", label: "Dedicated Travel Advisor" },
      ],
      ctaButtons: [
        { text: "Send Travel Inquiry", link: "/packages/inquiry", variant: "primary" },
        { text: "Contact Travel Team", link: "/contact", variant: "outline" },
      ],
    },
  },
  {
    key: "destinations",
    label: "Destination Stories",
    description: "The destination narrative carousel.",
    order: 12,
    defaultPayload: {
      eyebrow: "Destination Stories",
      title: "Discover Journeys That Stay With You",
      body: [
        "Travel should do more than move you. It should reconnect you to place, culture, and perspective.",
        "Each destination we design is selected for its ability to create depth, not just movement. The result is travel that feels grounded, immersive, and intentional.",
      ],
      destinations: [
        {
          eyebrow: "Uganda",
          title: "Queen Elizabeth National Park",
          body: [
            "One of Uganda's most versatile safari landscapes, where Kazinga Channel cruises, broad savannah, crater views, and Ishasha's tree-climbing lions create a cinematic route.",
            "The pace feels refined, the wildlife rhythm feels strong, and the itinerary can be shaped for both first-time and repeat safari travelers.",
          ],
          image:
            "https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1200&q=80",
          imageAlt: "Uganda destination story",
          verticalLabel: "#QUEENELIZABETH",
          destinationSlug: "queen-elizabeth-national-park",
        },
        {
          eyebrow: "Uganda",
          title: "Bwindi Impenetrable Forest",
          body: [
            "A destination built around gorilla trekking, rainforest atmosphere, and deeply memorable conservation-led travel.",
            "Bwindi is where Pearl Voyages can pair quiet luxury, purposeful pacing, and one of the most moving wildlife encounters in East Africa.",
          ],
          image:
            "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1200&q=80",
          imageAlt: "Bwindi destination story",
          verticalLabel: "#BWINDI",
          destinationSlug: "bwindi-impenetrable-forest",
        },
        {
          eyebrow: "Uganda",
          title: "Murchison Falls National Park",
          body: [
            "Where the Nile narrows through a dramatic gorge and the safari experience expands into broad savannah, riverbanks, and classic wildlife drives.",
            "This route works especially well for travelers who want iconic scenery and a stronger sense of movement across the landscape.",
          ],
          image:
            "https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?auto=format&fit=crop&w=1200&q=80",
          imageAlt: "Murchison Falls destination story",
          verticalLabel: "#MURCHISON",
          destinationSlug: "murchison-falls-national-park",
        },
        {
          eyebrow: "Uganda",
          title: "Kidepo Valley National Park",
          body: [
            "Remote, dramatic, and visually expansive, Kidepo is for travelers who want a stronger sense of wilderness and fewer distractions.",
            "It is the kind of destination that rewards deliberate routing and a slower, more immersive safari rhythm.",
          ],
          image:
            "https://images.unsplash.com/photo-1431274172761-fca41d930114?auto=format&fit=crop&w=1200&q=80",
          imageAlt: "Kidepo destination story",
          verticalLabel: "#KIDEPO",
          destinationSlug: "kidepo-valley-national-park",
        },
      ],
    },
  },
  {
    key: "awards",
    label: "Awards",
    description: "The recognition rail directly below the hero.",
    order: 12,
    defaultPayload: {
      eyebrow: "Renowned worldwide",
      title: "Pearl Voyages awards",
      description:
        "Pearl Voyages Travel is shaped by trusted partnerships, destination knowledge, and a service model designed to support meaningful journeys across cultures and continents.",
      ctaLabel: "See all",
      awards: [
        {
          badge: "01",
          title: "Virtuoso Award 2025",
          description: "Pearl Voyages Travel • Best Sustainability Community Partner",
        },
        {
          badge: "02",
          title: "CNT Best Places to Go",
          description: "Recognized for destination-led travel experiences",
        },
        {
          badge: "03",
          title: "Men's Journal",
          description: "Recognized among standout safari and travel operators",
        },
        {
          badge: "04",
          title: "World Travel 2025",
          description: "Leading safari and curated luxury travel experiences",
        },
        {
          badge: "05",
          title: "Hospitality Excellence",
          description: "Recognized for customer experience and service quality",
        },
        {
          badge: "06",
          title: "Destination Partner",
          description: "Trusted collaboration across tourism and travel ecosystems",
        },
      ],
    },
  },
  {
    key: "why-pearl",
    label: "Why Pearl Voyages",
    description: "Trust and delivery discipline section.",
    order: 13,
    defaultPayload: {
      eyebrow: "Why Pearl Voyages Travel",
      title: "This is where trust is established.",
      copy: "We focus on planning discipline, experience quality, and consistency across every journey.",
      experiences: [
        {
          title: "Private Game Drives",
          copy: "Days shaped around wildlife movement, light, and terrain.",
          icon: "Compass",
        },
        {
          title: "Tailored Lodge Selection",
          copy: "Properties chosen for atmosphere, service, and location logic.",
          icon: "Sparkles",
        },
        {
          title: "Conservation-Led Travel",
          copy: "Experiences aligned with communities and long-term sustainability.",
          icon: "Trees",
        },
        {
          title: "End-to-End Planning",
          copy: "From route design to on-ground hosting, everything stays aligned.",
          icon: "ShieldCheck",
        },
      ],
      trustItems: [
        { label: "Tailored journeys", value: "100%" },
        { label: "Regional expertise", value: "East Africa" },
        { label: "Guest support", value: "24/7" },
        { label: "Planning approach", value: "Private-led" },
      ],
    },
  },
  {
    key: "partnerships-authorities",
    label: "Partnerships & Authorities",
    description: "The authority and trust band before conversion.",
    order: 14,
    defaultPayload: {
      eyebrow: "Partnerships & Authorities",
      title: "Structured trust across Uganda and Sweden",
      intro:
        "Pearl Voyages Travel is positioned between destination depth and regulatory clarity. We present our cross-continental service model through authority alignment, credible travel structures, and partnership-led delivery that gives travelers more confidence before they even inquire.",
      ugandaAuthorities: [
        { name: "Uganda Tourism Board", short: "UTB" },
        { name: "Association of Uganda Tour Operators", short: "AUTO" },
        { name: "Uganda Wildlife Authority", short: "UWA" },
      ],
      swedenAuthorities: [
        { name: "Kammarkollegiet", short: "KK" },
        { name: "Bolagsverket", short: "BV" },
        { name: "Transportstyrelsen", short: "TS" },
        { name: "Skatteverket", short: "SKV" },
      ],
      trustCards: [
        {
          title: "Licensed and Structured",
          body:
            "Our operating model is built around recognized regulatory frameworks, formal registration, and consumer protection expectations in both Uganda and Sweden.",
          icon: "ShieldCheck",
        },
        {
          title: "Cross-Continental Coordination",
          body:
            "We design journeys that move with clarity between East Africa and Europe, combining local execution with international planning discipline.",
          icon: "Globe2",
        },
        {
          title: "Partnership-Led Delivery",
          body:
            "Every experience is strengthened by destination knowledge, local hosting networks, and institutional alignment that support smoother, more reliable travel.",
          icon: "Users2",
        },
      ],
    },
  },
  {
    key: "testimonials",
    label: "Testimonials",
    description: "Guest perspective and social proof.",
    order: 15,
    defaultPayload: {
      eyebrow: "Guest Perspective",
      title: "Confidence is built before the journey begins.",
      copy: "Travel is easier to trust when the process already feels clear, calm, and well considered.",
      testimonials: [
        {
          name: "Amelia R.",
          title: "United Kingdom",
          quote:
            "Everything felt controlled, calm, and exceptionally well timed. The journey had range but never felt rushed.",
        },
        {
          name: "Daniel & Sofia",
          title: "Spain",
          quote:
            "The inquiry process already felt premium. By the time we traveled, we trusted every detail.",
        },
        {
          name: "Michael T.",
          title: "Canada",
          quote:
            "It balanced wildlife, comfort, and privacy in a way most operators claim, but rarely deliver.",
        },
      ],
    },
  },
  {
    key: "inquiry",
    label: "Immersive Purpose",
    description: "The immersive purpose reveal above the footer.",
    order: 16,
    defaultPayload: {
      eyebrow: "OUR PURPOSE",
      heading:
        "World-class travel packaging,\nand two-way tourism flows\nbetween Africa and Europe\n(Schengen Region)",
      actionLabel: "Make My Inquiry Now",
      actionHref: "",
      backgroundImage: "https://cdn.pearlvoyages.com/home-page/hero-image-lion.webp",
    },
  },
];

export const HOMEPAGE_SECTION_MAP = HOMEPAGE_SECTION_DEFINITIONS.reduce<Record<string, HomepageSectionDefinition>>(
  (acc, item) => {
    acc[item.key] = item;
    return acc;
  },
  {}
);

export function getHomepageSectionDefinition(key: string) {
  return HOMEPAGE_SECTION_MAP[key];
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function mergeHomepagePayload<T>(fallback: T, payload?: unknown): T {
  if (!isPlainObject(fallback)) {
    return payload === undefined ? fallback : (payload as T);
  }

  if (!isPlainObject(payload)) {
    return fallback;
  }

  const result: Record<string, unknown> = { ...fallback };

  for (const [key, value] of Object.entries(payload)) {
    const current = result[key];

    if (Array.isArray(value)) {
      result[key] = value;
      continue;
    }

    if (isPlainObject(current) && isPlainObject(value)) {
      result[key] = mergeHomepagePayload(current, value);
      continue;
    }

    if (value !== undefined) {
      result[key] = value;
    }
  }

  return result as T;
}
